import React, { useEffect, useState } from 'react';
import useLudoGame from '../hooks/useLudoGame';
import LudoBoard from './LudoBoard';
import ChatPopup from './ChatPopup';
import ChatIcon from './ChatIcon';
import GameSidebar from './GameSidebar';
import LoadingSpinner from './LoadingSpinner';
import ConnectionStatus from './ConnectionStatus';

const LudoGame = ({ gameData, onNavigate }) => {
  const {
    gameState,
    connectionStatus,
    error,
    playerColor,
    currentPlayer,
    gameStatus,
    players,
    chatMessages,
    playerId,
    diceValue,
    possibleMoves,
    rollDice,
    movePiece,
    sendChatMessage,
    connectToRoom
  } = useLudoGame();

  const [isInitialized, setIsInitialized] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [lastReadMessageCount, setLastReadMessageCount] = useState(0);

  useEffect(() => {
    if (gameData && !isInitialized) {
      connectToRoom(gameData.roomId, gameData.playerName);
      setIsInitialized(true);
    }
  }, [gameData, isInitialized, connectToRoom]);

  // Track unread messages
  useEffect(() => {
    if (chatMessages.length > lastReadMessageCount && !isChatOpen) {
      setUnreadMessages(chatMessages.length - lastReadMessageCount);
    }
  }, [chatMessages.length, lastReadMessageCount, isChatOpen]);

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setUnreadMessages(0);
      setLastReadMessageCount(chatMessages.length);
    }
  };

  const handleBackToHome = () => {
    onNavigate('home');
  };

  if (!gameData) {
    return (
      <div className="container">
        <div className="error">No game data available</div>
      </div>
    );
  }

  if (connectionStatus === 'connecting') {
    return (
      <LoadingSpinner 
        fullScreen={true}
        message={`Connecting to room ${gameData.roomId}...`}
        size="large"
      />
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-screen">
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button onClick={handleBackToHome} className="primary-button">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <div className="game-header">
        <button onClick={handleBackToHome} className="back-button">
          ← Back to Home
        </button>
        <h2>Room: {gameData.roomId}</h2>
        <ConnectionStatus 
          isConnected={connectionStatus === 'connected'}
          isConnecting={connectionStatus === 'connecting'}
          lastDisconnect={connectionStatus === 'disconnected' ? new Date() : null}
        />
      </div>

      <div className="game-content">
        <div className="game-main">
          <LudoBoard
            gameState={gameState}
            playerColor={playerColor}
            currentPlayer={currentPlayer}
            possibleMoves={possibleMoves}
            diceValue={diceValue}
            onMovePiece={movePiece}
            onRollDice={rollDice}
          />
        </div>

        <div className="game-sidebar-container">
          <GameSidebar
            players={players}
            currentPlayer={currentPlayer}
            gameStatus={gameStatus}
            playerColor={playerColor}
            playerId={playerId}
          />
        </div>
      </div>

      {/* Chat System */}
      <ChatIcon
        onClick={handleChatToggle}
        hasNewMessage={unreadMessages > 0}
        messageCount={unreadMessages}
      />

      <ChatPopup
        isOpen={isChatOpen}
        onClose={handleChatToggle}
        chatMessages={chatMessages}
        onSendMessage={sendChatMessage}
        playerColor={playerColor}
        players={players}
      />

      {/* Game Status Overlay */}
      {gameStatus.isGameOver && (
        <div className="game-overlay">
          <div className="game-result">
            <h2>🎉 Game Over!</h2>
            <p>
              {gameStatus.winner === (players[playerId]?.name) 
                ? "Congratulations! You won!" 
                : `${gameStatus.winner} won the game!`
              }
            </p>
            <button onClick={handleBackToHome} className="primary-button">
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LudoGame;
